import apiRequest from "../../../../utils/apiClient";
import { getUserVideosUrl, createShareUrl } from "../../../constants";
import { FILM_REEL_SHARE, LIVE_SHARE_TOKEN } from "./filmReelShare";

/**
 * Drives the home-page demos (Letters B/K/N/O) — which video the live ET embed shows. Two kinds
 * of source:
 *   1. SAMPLES — public, already-processed ETs hosted on PROD (the Film Reel + any curated demos).
 *      These are embedded from the PROD origin on every environment, so a new/anonymous visitor
 *      (and preview envs, which have a separate DB) always sees a working editor. No re-upload.
 *   2. The logged-in user's own LIBRARY videos — get-or-create an export share on the CURRENT
 *      backend and point the iframe at it (same-origin).
 *
 * Cross-origin note: on PROD the sample stays same-origin (so the interactive demo can drive the
 * iframe); off-prod it loads from PROD absolute (visible, but not same-origin drivable).
 */

const SRC_KEY = "cc_demo_share_src";     // chosen embed src for the demos ("/s/<t>" or absolute)
const ID_KEY = "cc_demo_share_id";       // chosen selection id ("film", "s:<token>", or a transcript id)
const TOK_PREFIX = "cc_demo_share_tok_"; // per-transcript cached share token (avoid re-creating)
export const DEMO_SRC_EVENT = "cc-demo-src-changed";
export const OPEN_UPLOAD_EVENT = "cc-open-upload"; // "Upload a video" picked with nothing to show

// PROD frontend origin — public sample shares live here (their tokens are in the prod DB).
const PROD_ORIGIN = "https://cherryclipz-v2-frontend.onrender.com";
// Every origin that serves prod against the prod DB. The sample share tokens below live in that
// DB, so any of these can embed them same-origin (drivable). Added the cherryclipz.com family
// 2026-09-04 ahead of the domain cutover; without it the new domain would treat itself as
// "off-prod" and lose the drivable demo.
const PROD_ORIGINS = [PROD_ORIGIN, "https://app.cherryclipz.com", "https://cherryclipz.com", "https://www.cherryclipz.com"];
const FILM_REEL_ID = "film";

export type DemoVideo = { id: number; title: string };
export type DemoSample = { id: string; title: string; token: string };

/**
 * Curated public sample ETs shown to EVERY visitor (anonymous included). The Film Reel is always
 * first. To feature another already-processed video, share it (permission "export", access
 * "public") and add `{ id, title, token }` here — nothing else to wire.
 */
// Curated so every option demos WELL: each has real per-clip thumbnails and loads fast. The two
// dropped earlier (Biden/Trump debate = 4,000+ clips, and the NBA reel) were sparse-thumbnail AND
// heavy — they read as mostly-blank posters and were slow to open, so they're off the demo picker.
export const SAMPLES: DemoSample[] = [
  { id: FILM_REEL_ID, title: "🍒 Film Reel (sample)", token: LIVE_SHARE_TOKEN },
  { id: "fabel", title: "Fabel 5", token: "BJ1KA0JqT295NsuGrL1VFps2v2qSgdJeLr8yqqRnFvQ" },
  { id: "gershenfeld", title: "Gershenfeld", token: "bgQktPQACDR9nzGPfMuSFgs7S2uOF7B5LMMkTGBayrA" },
  { id: "learn", title: "Our Time to Learn", token: "P6dTSR7z83kjXKb70IbCDN9I-0Zo48jYb0iUExaz4T8" },
];

/**
 * Samples taken off the menu. "The Other Side of the Picture" left 2026-10-25: it contains the
 * lines about Isaac's father leaving, which are never shown in a demo. A browser that had picked
 * it remembers it in localStorage, so getDemoShareSrc drops a remembered retired one.
 */
const RETIRED_TOKENS = ["r4GFBTBBQ0HWUzoGSUUFwNkQLR1KWc9Wpdh57IZ3auM"];

const ls = {
  get(k: string): string | null { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k: string, v: string) { try { localStorage.setItem(k, v); } catch (e) { /* ignore */ } },
  del(k: string) { try { localStorage.removeItem(k); } catch (e) { /* ignore */ } },
};

function onProd(): boolean { try { return PROD_ORIGINS.indexOf(window.location.origin) !== -1; } catch (e) { return false; } }
function onLocalhost(): boolean { try { return window.location.hostname === "localhost"; } catch (e) { return false; } }

/** Embed src for a PROD-hosted public share token: same-origin on prod, absolute-to-prod elsewhere. */
function sampleEmbed(token: string): string {
  return onProd() ? `/s/${token}` : `${PROD_ORIGIN}/s/${token}`;
}

/**
 * The default Film Reel embed. On localhost we keep the LOCAL share (FILM_REEL_SHARE resolves to
 * the local token there); everywhere else we load the reel from PROD so it always works.
 */
export function getFilmReelEmbed(): string {
  if (onLocalhost()) return FILM_REEL_SHARE; // "/s/<local-token>" against the local backend
  return sampleEmbed(LIVE_SHARE_TOKEN);
}

export function isLoggedIn(): boolean {
  return !!ls.get("token");
}

export function getDemoShareSrc(): string {
  const stored = ls.get(SRC_KEY);
  if (stored && RETIRED_TOKENS.some((t) => stored.includes(t))) {
    ls.del(SRC_KEY);
    ls.del(ID_KEY);
    return getFilmReelEmbed();
  }
  return stored || getFilmReelEmbed();
}

/** The current selection id: "film", "s:<token>", or a stringified transcript id. */
export function getDemoSelectionId(): string {
  return ls.get(ID_KEY) || FILM_REEL_ID;
}

function announce(src: string) {
  try { window.dispatchEvent(new CustomEvent(DEMO_SRC_EVENT, { detail: src })); } catch (e) { /* ignore */ }
}

/** Reset the demos back to the sample Film Reel. Returns the src for convenience. */
export function resetToFilmReel(): string {
  const src = getFilmReelEmbed();
  ls.del(SRC_KEY); ls.del(ID_KEY);
  announce(src);
  return src;
}

/** Point the demos at a curated public sample (Film Reel or another featured ET). */
export function selectSample(sample: DemoSample): string {
  if (sample.id === FILM_REEL_ID) return resetToFilmReel();
  const src = sampleEmbed(sample.token);
  ls.set(SRC_KEY, src);
  ls.set(ID_KEY, `s:${sample.id}`);
  announce(src);
  return src;
}

/** "Upload a video" was picked: scroll to the page's upload box (or the top nav's Upload). */
export function requestUpload() {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_UPLOAD_EVENT)); // a page may also react to this
    const el = document.getElementById("cc-demo-upload");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" }); // fall back to the top nav's Upload
    }
  } catch (e) { /* ignore */ }
}

/** The logged-in user's transcribed library (id = transcript pk, title). [] when logged out. */
export async function fetchDemoLibrary(): Promise<DemoVideo[]> {
  if (!isLoggedIn()) return [];
  try {
    const res = await apiRequest(getUserVideosUrl);
    if (!res.ok) return [];
    const rows = await res.json();
    if (!Array.isArray(rows)) return [];
    return rows
      // Only offer videos that actually have per-clip thumbnails. A thumbnail-less ET (audio-only,
      // or posters still generating) renders BLANK in the demo embed — the "where are the
      // thumbnails?!" false alarm. The library API returns "" for `thumbnail` when the first clip
      // has no poster, so a non-empty thumbnail is the reliable "this demos well" signal.
      .filter((r: any) => r && r.id != null && typeof r.thumbnail === "string" && r.thumbnail.length > 0)
      .map((r: any) => ({ id: Number(r.id), title: String(r.title || `Video ${r.id}`) }));
  } catch (e) {
    return [];
  }
}

/**
 * Point the demos at a library video: reuse a cached export-share token for that transcript,
 * or create one via share/create/. Persists the choice, notifies listeners, returns "/s/<token>".
 */
export async function selectDemoVideo(transcriptId: number): Promise<string> {
  const cacheKey = TOK_PREFIX + transcriptId;
  let path = ls.get(cacheKey);
  if (!path) {
    const body = new URLSearchParams({ transcript_id: String(transcriptId), permission_level: "export" });
    const res = await apiRequest(createShareUrl, { method: "POST", body });
    if (!res.ok) throw new Error("share-create-failed");
    const data = await res.json();
    path = data.url_path || (data.token ? `/s/${data.token}` : "");
    if (!path) throw new Error("share-no-token");
    ls.set(cacheKey, path);
  }
  ls.set(SRC_KEY, path);
  ls.set(ID_KEY, String(transcriptId));
  announce(path);
  return path;
}
